import axios from 'axios';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { bulkApi } from 'organisms/CloudOpsReportAdmin/bulk/api';
import { MAX_BULK_REPORT_SELECTION } from 'organisms/CloudOpsReportAdmin/bulk/constants';
import { pollBulkJob } from 'organisms/CloudOpsReportAdmin/bulk/pollBulkJob';
import {
  BulkJobAction,
  BulkProgressState,
  BulkWorkflowSession,
  ProjectReportBulkJob,
  TriggerBulkActionResponse,
} from 'organisms/CloudOpsReportAdmin/bulk/types';
import { useReportBulkSelection } from 'organisms/CloudOpsReportAdmin/bulk/useReportBulkSelection';
import { Report } from 'organisms/CloudOpsReportAdmin/types';
import {
  getBulkActionPresentation,
  progressFromBulkJob,
  resultFromBulkJob,
} from 'organisms/CloudOpsReportAdmin/bulk/actionPresentation';

export interface BulkApi {
  trigger: typeof bulkApi.trigger;
  getJob: typeof bulkApi.getJob;
  listJobs: typeof bulkApi.listJobs;
}

export interface BulkReportActionOptions {
  currentPageReports: Report[];
  resetKey: string;
  refreshReports: () => Promise<void>;
  enabled?: boolean;
  api?: BulkApi;
  waitOverride?: (milliseconds: number) => Promise<void>;
}

interface WorkflowOperation {
  id: number;
}

interface LegacyBulkWorkflowSession {
  phase: BulkJobAction;
  actionJobId: number | null;
  startedAt: string;
  reportIds: number[];
}

type RecoverableBulkWorkflowSession =
  | BulkWorkflowSession
  | LegacyBulkWorkflowSession;

const SESSION_KEY = 'project-reports-bulk-approve-update';

const sameIds = (left: number[], right: number[]) =>
  [...left].sort((a, b) => a - b).join(',') ===
  [...right].sort((a, b) => a - b).join(',');

const matchesOwnedAction = (
  job: ProjectReportBulkJob,
  action: BulkJobAction,
  jobId: number,
  reportIds: number[]
) =>
  job.id === jobId &&
  job.action === action &&
  Array.isArray(job.requested_report_ids) &&
  sameIds(job.requested_report_ids, reportIds) &&
  Array.isArray(job.items) &&
  job.items.every(
    (jobItem) =>
      jobItem.job_id === jobId && reportIds.includes(jobItem.report_id)
  );

const getConflictJobId = (error: unknown): number | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return null;
  const data = error.response.data as TriggerBulkActionResponse;
  return Number.isFinite(data.job_id) ? data.job_id : null;
};

const getHttpStatus = (error: unknown): number | null => {
  if (axios.isAxiosError(error)) {
    return error.response?.status ?? null;
  }
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return null;
  }
  const status = (error as { response?: { status?: unknown } }).response
    ?.status;
  return typeof status === 'number' ? status : null;
};

const isRecoverableStoredSession = (
  stored: unknown
): stored is RecoverableBulkWorkflowSession => {
  if (typeof stored !== 'object' || stored === null) return false;
  const candidate = stored as Record<string, unknown>;
  const hasValidReportIds =
    Array.isArray(candidate.reportIds) &&
    candidate.reportIds.length > 0 &&
    candidate.reportIds.every(
      (reportId) => typeof reportId === 'number' && Number.isFinite(reportId)
    );
  const hasValidStartedAt =
    typeof candidate.startedAt === 'string' &&
    Number.isFinite(new Date(candidate.startedAt).getTime());
  if (!hasValidReportIds || !hasValidStartedAt) return false;

  const storedAction = candidate.action || candidate.phase;
  const hasValidAction =
    storedAction === 'approve' || storedAction === 'update';
  if (!hasValidAction) return false;

  if (candidate.action !== undefined) {
    return (
      typeof candidate.actionJobId === 'number' &&
      Number.isFinite(candidate.actionJobId)
    );
  }

  return (
    (candidate.phase === 'approve' || candidate.phase === 'update') &&
    ((candidate.phase === 'approve' && candidate.actionJobId === null) ||
      (typeof candidate.actionJobId === 'number' &&
        Number.isFinite(candidate.actionJobId)))
  );
};

const getStoredAction = (stored: RecoverableBulkWorkflowSession) =>
  'action' in stored ? stored.action : stored.phase;

export function useBulkReportAction({
  currentPageReports,
  resetKey,
  refreshReports,
  enabled = true,
  api = bulkApi,
  waitOverride,
}: BulkReportActionOptions) {
  const selection = useReportBulkSelection({
    currentPageReports,
    resetKey,
  });
  const { approvableIds, clearSelection, selectedIds, updatableIds } =
    selection;
  const [confirmationAction, setConfirmationAction] =
    useState<BulkJobAction | null>(null);
  const [workflowActive, setWorkflowActive] = useState(false);
  const [progress, setProgress] = useState<BulkProgressState | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const waitResolverRef = useRef<(() => void) | null>(null);
  const cancelledRef = useRef(false);
  const submitLockRef = useRef(false);
  const workflowEpochRef = useRef(0);
  const previousEnabledRef = useRef(enabled);
  const recoveryEpochRef = useRef<number | null>(null);
  const enabledRef = useRef(enabled);
  const refreshReportsRef = useRef(refreshReports);
  const operationSequenceRef = useRef(0);
  const sessionOwnerRef = useRef<{
    operation: WorkflowOperation;
    raw: string | null;
  } | null>(null);
  enabledRef.current = enabled;
  refreshReportsRef.current = refreshReports;

  const workflowUnavailable = useCallback(
    (epoch = workflowEpochRef.current) =>
      cancelledRef.current ||
      !enabledRef.current ||
      epoch !== workflowEpochRef.current,
    []
  );

  useLayoutEffect(() => {
    if (previousEnabledRef.current === enabled) return;
    workflowEpochRef.current += 1;
    previousEnabledRef.current = enabled;
  }, [enabled]);

  const createOperation = useCallback((raw: string | null) => {
    const operation = { id: operationSequenceRef.current + 1 };
    operationSequenceRef.current = operation.id;
    sessionOwnerRef.current = { operation, raw };
    return operation;
  }, []);

  const operationForStoredSession = useCallback(
    (raw: string) => {
      const owner = sessionOwnerRef.current;
      return owner?.raw === raw ? owner.operation : createOperation(raw);
    },
    [createOperation]
  );

  const replaceOwnedSession = useCallback(
    (
      operation: WorkflowOperation,
      expectedRaw: string | null,
      next: BulkWorkflowSession
    ) => {
      const owner = sessionOwnerRef.current;
      if (owner?.operation !== operation) return null;

      const nextRaw = JSON.stringify(next);
      const storedRaw = sessionStorage.getItem(SESSION_KEY);
      if (owner.raw === nextRaw && storedRaw === nextRaw) return nextRaw;
      if (owner.raw !== expectedRaw || storedRaw !== expectedRaw) return null;

      sessionStorage.setItem(SESSION_KEY, nextRaw);
      owner.raw = nextRaw;
      return nextRaw;
    },
    []
  );

  const removeOwnedSession = useCallback(
    (operation: WorkflowOperation, expectedRaw: string) => {
      const owner = sessionOwnerRef.current;
      if (
        owner?.operation !== operation ||
        owner.raw !== expectedRaw ||
        sessionStorage.getItem(SESSION_KEY) !== expectedRaw
      ) {
        return false;
      }

      sessionStorage.removeItem(SESSION_KEY);
      owner.raw = null;
      return true;
    },
    []
  );

  const wait = useCallback(
    (milliseconds: number) =>
      new Promise<void>((resolve) => {
        waitResolverRef.current = resolve;
        timeoutRef.current = window.setTimeout(() => {
          timeoutRef.current = null;
          waitResolverRef.current = null;
          resolve();
        }, milliseconds);
      }),
    []
  );

  const waitForPoll = waitOverride || wait;

  const updateProgress = useCallback(
    (next: BulkProgressState, epoch: number) => {
      if (!workflowUnavailable(epoch)) setProgress(next);
    },
    [workflowUnavailable]
  );

  const refreshCompletedWorkflow = useCallback(
    async (action: BulkJobAction, epoch: number) => {
      try {
        await refreshReportsRef.current();
        return !workflowUnavailable(epoch);
      } catch (error) {
        if (workflowUnavailable(epoch)) return false;
        updateProgress(
          {
            phase: 'completed',
            severity: 'warning',
            terminal: true,
            message: `Bulk ${
              getBulkActionPresentation(action).noun
            } completed, but the report table could not be refreshed. Reload to see the latest results.`,
          },
          epoch
        );
        return false;
      }
    },
    [updateProgress, workflowUnavailable]
  );

  useEffect(
    () => () => {
      cancelledRef.current = true;
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      waitResolverRef.current?.();
      waitResolverRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (enabled) return;

    submitLockRef.current = false;
    clearSelection();
    setConfirmationAction(null);
    setWorkflowActive(false);
    setProgress(null);
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    waitResolverRef.current?.();
    waitResolverRef.current = null;
  }, [clearSelection, enabled]);

  const openConfirmation = useCallback(
    (action: BulkJobAction) => {
      const actionReportIds =
        action === 'approve' ? approvableIds : updatableIds;
      if (
        actionReportIds.length > 0 &&
        !workflowUnavailable() &&
        !workflowActive &&
        selectedIds.length > 0 &&
        selectedIds.length <= MAX_BULK_REPORT_SELECTION
      ) {
        setConfirmationAction(action);
      }
    },
    [
      approvableIds,
      selectedIds.length,
      updatableIds,
      workflowActive,
      workflowUnavailable,
    ]
  );

  const closeConfirmation = useCallback(() => {
    if (!workflowUnavailable() && !workflowActive) {
      setConfirmationAction(null);
    }
  }, [workflowActive, workflowUnavailable]);

  const dismissProgress = useCallback(() => {
    if (workflowUnavailable()) return;
    setProgress((current) => (current?.terminal ? null : current));
  }, [workflowUnavailable]);

  const finishOwnedWorkflow = useCallback(
    async (
      actionJob: ProjectReportBulkJob,
      epoch: number,
      operation: WorkflowOperation,
      ownedSessionRaw: string
    ) => {
      if (workflowUnavailable(epoch)) return;
      if (!removeOwnedSession(operation, ownedSessionRaw)) return;

      clearSelection();
      const refreshed = await refreshCompletedWorkflow(actionJob.action, epoch);
      if (!refreshed) return;
      setProgress(resultFromBulkJob(actionJob));
    },
    [
      clearSelection,
      refreshCompletedWorkflow,
      removeOwnedSession,
      workflowUnavailable,
    ]
  );

  const followExternalJob = useCallback(
    async (jobId: number, epoch: number) => {
      updateProgress(
        {
          phase: 'conflict',
          severity: 'warning',
          terminal: false,
          message: 'Another bulk job is already running.',
        },
        epoch
      );

      const activeJob = await pollBulkJob({
        jobId,
        getJob: api.getJob,
        wait: waitForPoll,
        isCancelled: () => workflowUnavailable(epoch),
        onProgress: (currentJob) => {
          updateProgress(
            {
              phase: 'conflict',
              severity: 'warning',
              terminal: false,
              processed: currentJob.processed_count,
              total: currentJob.total_count,
              message: `Existing ${currentJob.action} job: ${currentJob.processed_count} of ${currentJob.total_count} completed.`,
            },
            epoch
          );
        },
      });

      updateProgress(
        {
          phase: 'completed',
          severity: 'warning',
          terminal: true,
          message: `Existing ${activeJob.action} job finished. Retry your selected reports when ready.`,
        },
        epoch
      );
    },
    [api.getJob, updateProgress, waitForPoll, workflowUnavailable]
  );

  const runOwnedAction = useCallback(
    async (
      action: BulkJobAction,
      jobId: number,
      reportIds: number[],
      epoch: number,
      operation: WorkflowOperation,
      actionSessionRaw: string
    ) => {
      if (workflowUnavailable(epoch)) return;

      const actionJob = await pollBulkJob({
        jobId,
        getJob: api.getJob,
        wait: waitForPoll,
        isCancelled: () => workflowUnavailable(epoch),
        onProgress: (currentJob) =>
          updateProgress(progressFromBulkJob(currentJob), epoch),
      });
      if (workflowUnavailable(epoch)) return;

      if (!matchesOwnedAction(actionJob, action, jobId, reportIds)) {
        if (!removeOwnedSession(operation, actionSessionRaw)) return;
        updateProgress(
          {
            phase: 'error',
            severity: 'error',
            terminal: true,
            message: `The stored bulk ${
              getBulkActionPresentation(action).noun
            } did not match the requested reports and was cleared.`,
          },
          epoch
        );
        return;
      }

      await finishOwnedWorkflow(actionJob, epoch, operation, actionSessionRaw);
    },
    [
      api,
      finishOwnedWorkflow,
      removeOwnedSession,
      updateProgress,
      waitForPoll,
      workflowUnavailable,
    ]
  );

  const discoverLegacyApproval = useCallback(
    async (
      stored: LegacyBulkWorkflowSession,
      storedRaw: string,
      epoch: number,
      operation: WorkflowOperation
    ) => {
      try {
        const listed = await api.listJobs(1, 20);
        if (workflowUnavailable(epoch)) return undefined;
        const matches = listed.jobs.filter(
          (candidate) =>
            candidate.action === 'approve' &&
            Boolean(candidate.created_at) &&
            new Date(candidate.created_at as string).getTime() >=
              new Date(stored.startedAt).getTime() &&
            sameIds(
              candidate.reports.map((report) => report.report_id),
              stored.reportIds
            )
        );
        if (matches.length !== 1) {
          updateProgress(
            {
              phase: 'error',
              severity: 'error',
              terminal: true,
              message: 'Unable to recover the interrupted approval safely.',
            },
            epoch
          );
          return undefined;
        }

        const jobId = matches[0].id;
        const recoveredSessionRaw = replaceOwnedSession(operation, storedRaw, {
          action: 'approve',
          actionJobId: jobId,
          startedAt: stored.startedAt,
          reportIds: stored.reportIds,
        });
        return recoveredSessionRaw
          ? { jobId, sessionRaw: recoveredSessionRaw }
          : undefined;
      } catch (error) {
        if (workflowUnavailable(epoch)) return undefined;
        updateProgress(
          {
            phase: 'error',
            severity: 'warning',
            terminal: true,
            message: 'Recovery could not be completed. Reload to continue.',
          },
          epoch
        );
        return undefined;
      }
    },
    [api, replaceOwnedSession, updateProgress, workflowUnavailable]
  );

  const recoverStoredWorkflow = useCallback(
    async (epoch: number) => {
      if (workflowUnavailable(epoch) || recoveryEpochRef.current === epoch) {
        return;
      }
      recoveryEpochRef.current = epoch;

      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) {
        if (recoveryEpochRef.current === epoch) {
          recoveryEpochRef.current = null;
        }
        return;
      }
      const operation = operationForStoredSession(raw);

      if (!workflowUnavailable(epoch)) setWorkflowActive(true);
      let stored: RecoverableBulkWorkflowSession;
      try {
        const parsed: unknown = JSON.parse(raw);
        if (!isRecoverableStoredSession(parsed)) {
          throw new Error('Invalid stored bulk workflow');
        }
        stored = parsed;
      } catch (error) {
        if (workflowUnavailable(epoch) || !removeOwnedSession(operation, raw)) {
          return;
        }
        updateProgress(
          {
            phase: 'error',
            severity: 'error',
            terminal: true,
            message:
              'The stored bulk workflow was invalid and has been cleared.',
          },
          epoch
        );
        setWorkflowActive(false);
        return;
      }

      let actionSessionRaw = raw;
      const action = getStoredAction(stored);
      let jobId = stored.actionJobId;

      try {
        if (jobId === null) {
          if (!('phase' in stored) || action !== 'approve') return;
          const discovered = await discoverLegacyApproval(
            stored,
            raw,
            epoch,
            operation
          );
          if (!discovered) return;
          jobId = discovered.jobId;
          actionSessionRaw = discovered.sessionRaw;
        }

        await runOwnedAction(
          action,
          jobId,
          stored.reportIds,
          epoch,
          operation,
          actionSessionRaw
        );
      } catch (error) {
        if (workflowUnavailable(epoch)) return;
        if (getHttpStatus(error) === 404) {
          if (!removeOwnedSession(operation, actionSessionRaw)) return;
          updateProgress(
            {
              phase: 'error',
              severity: 'error',
              terminal: true,
              message:
                'The stored bulk workflow no longer exists and was cleared.',
            },
            epoch
          );
        } else {
          updateProgress(
            {
              phase: 'error',
              severity: 'warning',
              terminal: true,
              message: 'Recovery could not be completed. Reload to continue.',
            },
            epoch
          );
        }
      } finally {
        if (!workflowUnavailable(epoch)) setWorkflowActive(false);
      }
    },
    [
      discoverLegacyApproval,
      operationForStoredSession,
      removeOwnedSession,
      runOwnedAction,
      updateProgress,
      workflowUnavailable,
    ]
  );

  useEffect(() => {
    if (enabled) recoverStoredWorkflow(workflowEpochRef.current);
  }, [enabled, recoverStoredWorkflow]);

  const confirm = useCallback(async () => {
    const epoch = workflowEpochRef.current;
    const submissionSessionRaw = sessionStorage.getItem(SESSION_KEY);
    const action = confirmationAction;
    let actionReportIds: number[] = [];
    if (action === 'approve') actionReportIds = approvableIds;
    if (action === 'update') actionReportIds = updatableIds;
    if (
      workflowUnavailable(epoch) ||
      submitLockRef.current ||
      selectedIds.length === 0 ||
      selectedIds.length > MAX_BULK_REPORT_SELECTION ||
      action === null ||
      actionReportIds.length === 0 ||
      submissionSessionRaw !== null
    ) {
      return;
    }
    const operation = createOperation(submissionSessionRaw);
    submitLockRef.current = true;
    const reportIds = [...actionReportIds];
    const startedAt = new Date().toISOString();
    setWorkflowActive(true);

    try {
      let response: TriggerBulkActionResponse;
      try {
        response = await api.trigger(action, reportIds);
      } catch (error) {
        if (workflowUnavailable(epoch)) return;
        const conflictId = getConflictJobId(error);
        if (conflictId !== null) {
          setConfirmationAction(null);
          try {
            await followExternalJob(conflictId, epoch);
          } catch (conflictError) {
            if (workflowUnavailable(epoch)) return;
            updateProgress(
              {
                phase: 'error',
                severity: 'error',
                terminal: true,
                message:
                  'The existing bulk job could not be monitored. Please try again.',
              },
              epoch
            );
          }
        } else {
          updateProgress(
            {
              phase: 'error',
              severity: 'error',
              terminal: true,
              message: `Failed to start bulk ${
                getBulkActionPresentation(action).noun
              }. Please try again.`,
            },
            epoch
          );
        }
        return;
      }

      const actionSessionRaw = replaceOwnedSession(
        operation,
        submissionSessionRaw,
        {
          action,
          actionJobId: response.job_id,
          startedAt,
          reportIds,
        }
      );
      if (!actionSessionRaw) return;
      if (workflowUnavailable(epoch)) {
        const currentEpoch = workflowEpochRef.current;
        if (!workflowUnavailable(currentEpoch)) {
          recoverStoredWorkflow(currentEpoch);
        }
        return;
      }
      setConfirmationAction(null);

      try {
        await runOwnedAction(
          action,
          response.job_id,
          reportIds,
          epoch,
          operation,
          actionSessionRaw
        );
      } catch (error) {
        if (workflowUnavailable(epoch)) return;
        updateProgress(
          {
            phase: 'error',
            severity: 'error',
            terminal: true,
            message: `Bulk ${
              getBulkActionPresentation(action).noun
            } was accepted, but the workflow could not be completed. Reload to recover the workflow.`,
          },
          epoch
        );
      }
    } finally {
      submitLockRef.current = false;
      if (!workflowUnavailable(epoch)) setWorkflowActive(false);
    }
  }, [
    api,
    approvableIds,
    confirmationAction,
    createOperation,
    followExternalJob,
    recoverStoredWorkflow,
    replaceOwnedSession,
    runOwnedAction,
    selectedIds,
    updatableIds,
    updateProgress,
    workflowUnavailable,
  ]);

  return {
    ...selection,
    confirmationAction,
    workflowActive,
    progress,
    openConfirmation,
    closeConfirmation,
    confirm,
    dismissProgress,
  };
}
